/**
 * Consistent page container using layout tokens.
 * Use this instead of hardcoding maxWidth/padding on every page.
 *
 * Sizes:
 *   page    — 1200px (dashboard, landing inner sections)
 *   content: 720px  (module reading, articles)
 *   form    — 540px  (login, profile, checkout)
 *   dialog  — 480px  (paywall, 404, modals)
 *
 * Alineacion:
 *   center — centrado (por defecto)
 *   start  — pegado al mismo borde izquierdo que la barra de
 *            navegacion, dentro del ancho de pagina
 *
 * 'start' existe porque una columna estrecha centrada no cuadra con
 * una cabecera ancha: el logo queda a la izquierda del todo y el
 * titulo de la pagina flota en medio, con un hueco delante que parece
 * un error. Para pantallas de cuenta y ajustes, donde el contenido es
 * una columna de filas, 'start' hace que el titulo empiece justo
 * debajo del logo.
 */

import type { ReactNode, CSSProperties } from 'react'

interface Props {
  children: ReactNode
  size?: 'page' | 'content' | 'form' | 'dialog'
  align?: 'center' | 'start'
  className?: string
  style?: CSSProperties
  as?: 'div' | 'section'
}

const SIZE_VAR: Record<NonNullable<Props['size']>, string> = {
  page: 'var(--mw-w-page)',
  content: 'var(--mw-w-content)',
  form: 'var(--mw-w-form)',
  dialog: 'var(--mw-w-dialog)',
}

export function MwContainer({
  children,
  size = 'page',
  align = 'center',
  className,
  style,
  as: Tag = 'div',
}: Props) {
  if (align === 'start') {
    // Dos cajas: la de fuera da el ancho de pagina y el borde comun
    // con la cabecera; la de dentro limita la lectura.
    return (
      <div style={{ maxWidth: 'var(--mw-w-page)', margin: '0 auto', padding: '0 var(--mw-p-page)' }}>
        <Tag className={className} style={{ maxWidth: SIZE_VAR[size], ...style }}>
          {children}
        </Tag>
      </div>
    )
  }

  return (
    <Tag
      className={className}
      style={{
        maxWidth: SIZE_VAR[size],
        margin: '0 auto',
        padding: '0 var(--mw-p-page)',
        ...style,
      }}
    >
      {children}
    </Tag>
  )
}
